import { useEffect, useId, useRef } from 'react'
import { createPortal } from 'react-dom'

// ── Button ────────────────────────────────────────────────────────────────────
export function Button({ children, variant = 'primary', size = 'md', className = '', ...props }) {
  const base = 'inline-flex min-h-11 min-w-11 items-center justify-center gap-2 font-semibold rounded-lg transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed'
  const variants = {
    primary: 'bg-gold text-charcoal-900 hover:bg-gold-light active:bg-gold-dark',
    ghost: 'border border-white/20 text-cream/80 hover:border-gold hover:text-gold',
    danger: 'bg-red-900/30 text-red-400 border border-red-900/50 hover:bg-red-900/50',
  }
  const sizes = { sm: 'px-3 py-1.5 text-sm', md: 'px-4 py-2', lg: 'px-6 py-3 text-lg' }
  return (
    <button className={`${base} ${variants[variant]} ${sizes[size]} ${className}`} {...props}>
      {children}
    </button>
  )
}

// ── Badge ─────────────────────────────────────────────────────────────────────
export function Badge({ children, color = 'gold', className = '' }) {
  const colors = {
    gold: 'bg-gold/20 text-gold',
    sage: 'bg-sage/20 text-sage',
    blue: 'bg-blue-900/40 text-blue-300',
    red: 'bg-red-900/40 text-red-400',
    muted: 'bg-charcoal-600 text-cream/60',
  }
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${colors[color]} ${className}`}>
      {children}
    </span>
  )
}

// ── Input ─────────────────────────────────────────────────────────────────────
export function Input({ label, error, className = '', ...props }) {
  return (
    <div className="flex flex-col gap-1">
      {label && <label className="text-xs text-cream/60 font-medium uppercase tracking-wide">{label}</label>}
      <input
        className={`min-h-11 w-full bg-charcoal-600 border ${error ? 'border-red-500' : 'border-white/10'} rounded-lg px-3 py-2 text-cream placeholder-cream/30 focus:outline-none focus:border-gold transition-colors ${className}`}
        {...props}
      />
      {error && <span className="text-xs text-red-400">{error}</span>}
    </div>
  )
}

// ── Select ────────────────────────────────────────────────────────────────────
export function Select({ label, options = [], className = '', ...props }) {
  return (
    <div className="flex flex-col gap-1">
      {label && <label className="text-xs text-cream/60 font-medium uppercase tracking-wide">{label}</label>}
      <select
        className={`min-h-11 w-full bg-charcoal-600 border border-white/10 rounded-lg px-3 py-2 text-cream focus:outline-none focus:border-gold transition-colors ${className}`}
        {...props}
      >
        {options.map(o => (
          <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
        ))}
      </select>
    </div>
  )
}

// ── Modal ─────────────────────────────────────────────────────────────────────
export function Modal({
  open,
  onClose,
  title,
  children,
  maxWidth = 'max-w-lg',
  fullScreenMobile = false,
  updateEntry = false,
  bodyClassName: bodyClassNameOverride = '',
}) {
  const dialogRef = useRef(null)
  const onCloseRef = useRef(onClose)
  const titleId = useId()
  onCloseRef.current = onClose

  useEffect(() => {
    if (!open) return undefined

    const body = document.body
    const root = document.documentElement
    const scrollY = window.scrollY
    const previousBodyStyles = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
    }

    // These variables size and position the dialog only. The backdrop remains
    // attached to the full layout viewport so content cannot show through in
    // the space between a reduced iOS visual viewport and the keyboard.
    const syncVisualViewport = () => {
      const viewport = window.visualViewport
      const viewportHeight = viewport?.height || root.clientHeight
      const viewportOffset = viewport?.offsetTop || 0
      root.style.setProperty('--modal-viewport-height', `${viewportHeight}px`)
      root.style.setProperty('--modal-viewport-offset', `${viewportOffset}px`)
      // Bottom sheets sit at the bottom of the full-height backdrop, which is
      // behind the iOS keyboard. Measure how far the backdrop extends below the
      // visible area so the sheet can be lifted to sit on top of the keyboard.
      const backdropBottom = dialogRef.current?.parentElement?.getBoundingClientRect().bottom
      const keyboardInset = viewport && backdropBottom
        ? Math.max(0, backdropBottom - (viewportOffset + viewportHeight))
        : 0
      root.style.setProperty('--modal-keyboard-inset', `${Math.round(keyboardInset)}px`)
    }
    const revealActiveControl = () => {
      window.requestAnimationFrame(() => {
        const activeControl = document.activeElement
        if (activeControl && dialogRef.current?.contains(activeControl)) {
          activeControl.scrollIntoView({ block: 'nearest', inline: 'nearest' })
        }
      })
    }
    const handleViewportResize = () => {
      syncVisualViewport()
      revealActiveControl()
    }
    // Moving between fields while the keyboard is already open fires no
    // viewport resize, so reveal the newly focused control directly.
    const handleFocusIn = (event) => {
      if (event.target?.matches?.('input, textarea, select, [contenteditable="true"]')) revealActiveControl()
    }

    body.style.position = 'fixed'
    body.style.top = `-${scrollY}px`
    body.style.left = '0'
    body.style.right = '0'
    body.style.width = '100%'
    body.style.overflow = 'hidden'
    syncVisualViewport()

    window.visualViewport?.addEventListener('resize', handleViewportResize)
    window.visualViewport?.addEventListener('scroll', syncVisualViewport)
    window.addEventListener('resize', handleViewportResize)
    document.addEventListener('focusin', handleFocusIn)

    return () => {
      window.visualViewport?.removeEventListener('resize', handleViewportResize)
      window.visualViewport?.removeEventListener('scroll', syncVisualViewport)
      window.removeEventListener('resize', handleViewportResize)
      document.removeEventListener('focusin', handleFocusIn)
      root.style.removeProperty('--modal-viewport-height')
      root.style.removeProperty('--modal-viewport-offset')
      root.style.removeProperty('--modal-keyboard-inset')
      Object.assign(body.style, previousBodyStyles)
      window.scrollTo(0, scrollY)
    }
  }, [open])

  useEffect(() => {
    if (!open) return undefined

    const previousFocus = document.activeElement
    const focusableSelector = [
      'button:not([disabled])',
      '[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',')

    const focusInitialControl = window.requestAnimationFrame(() => {
      const dialog = dialogRef.current
      const target = dialog?.querySelector('[autofocus]') || dialog?.querySelector(focusableSelector)
      target?.focus({ preventScroll: true })
    })

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onCloseRef.current?.()
        return
      }
      if (event.key !== 'Tab') return

      const focusable = [...(dialogRef.current?.querySelectorAll(focusableSelector) || [])]
        .filter(element => !element.hasAttribute('hidden') && element.getAttribute('aria-hidden') !== 'true')
      if (focusable.length === 0) {
        event.preventDefault()
        dialogRef.current?.focus()
        return
      }

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      window.cancelAnimationFrame(focusInitialControl)
      document.removeEventListener('keydown', handleKeyDown)
      previousFocus?.focus?.({ preventScroll: true })
    }
  }, [open])

  if (!open) return null

  // On phones, add flows fill the visual viewport so the keyboard cannot hide
  // the primary action. Other dialogs keep the bottom-sheet presentation.
  const backdropClassName = fullScreenMobile
    ? 'modal-backdrop modal-backdrop-fullscreen items-stretch p-0 sm:items-center sm:p-4'
    : 'modal-backdrop'
  const boxClassName = fullScreenMobile
    ? `modal-box modal-box-fullscreen ${maxWidth} w-full rounded-none sm:max-h-[90vh] sm:rounded-2xl sm:overflow-y-auto`
    : `modal-box ${maxWidth}`
  const defaultBodyClassName = fullScreenMobile
    ? 'modal-body px-6 py-4 sm:flex-none sm:min-h-fit sm:overflow-visible'
    : 'modal-body px-4 py-4 sm:px-6'
  const bodyClassName = `${defaultBodyClassName} ${bodyClassNameOverride}`.trim()

  return createPortal(
    <div
      className={backdropClassName}
      data-modal-backdrop
      data-update-blocking="true"
      onClick={() => onCloseRef.current?.()}
    >
      <div
        ref={dialogRef}
        data-modal-box
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        className={boxClassName}
        onClick={event => event.stopPropagation()}
      >
        {title && (
          <div
            className="flex shrink-0 items-center justify-between border-b border-white/10 px-4 py-2 sm:px-6 sm:py-3"
            style={fullScreenMobile ? { paddingTop: 'max(env(safe-area-inset-top), 10px)' } : undefined}
          >
            <h2 id={titleId} className="font-body text-lg text-gold">{title}</h2>
            <button
              type="button"
              aria-label="Close dialog"
              onClick={() => onCloseRef.current?.()}
              className="flex h-11 w-11 items-center justify-center rounded-lg text-xl leading-none text-cream/40 transition-colors hover:bg-white/5 hover:text-cream"
            >
              ×
            </button>
          </div>
        )}
        <div
          className={bodyClassName}
          data-update-entry={updateEntry ? 'true' : undefined}
          style={fullScreenMobile ? { paddingBottom: 'max(env(safe-area-inset-bottom), 16px)' } : undefined}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body,
  )
}

// ── RatingBar ─────────────────────────────────────────────────────────────────
export function RatingBar({ label, value, max = 10, color = '#D4AF37' }) {
  const pct = Math.round((value / max) * 100)
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-cream/50 w-20 shrink-0">{label}</span>
      <div className="flex-1 h-1.5 bg-charcoal-600 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500"
             style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="text-xs font-mono text-cream/70 w-6 text-right">{value}</span>
    </div>
  )
}

// ── Spinner ───────────────────────────────────────────────────────────────────
export function Spinner({ size = 'md' }) {
  const s = { sm: 'w-4 h-4', md: 'w-6 h-6', lg: 'w-10 h-10' }[size]
  return (
    <svg className={`${s} animate-spin text-gold`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
    </svg>
  )
}

// ── EmptyState ────────────────────────────────────────────────────────────────
export function EmptyState({ icon = '💧', title, body, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center gap-4 animate-fade-in">
      <div className="text-5xl">{icon}</div>
      <h3 className="font-body text-xl text-cream/70">{title}</h3>
      {body && <p className="text-cream/40 text-sm max-w-xs">{body}</p>}
      {action}
    </div>
  )
}

// ── Skeleton Card ─────────────────────────────────────────────────────────────
export function SkeletonCard() {
  return (
    <div className="card p-4 flex flex-col gap-3">
      <div className="skeleton h-40 w-full rounded-lg" />
      <div className="skeleton h-4 w-3/4 rounded" />
      <div className="skeleton h-3 w-1/2 rounded" />
      <div className="flex gap-1">
        <div className="skeleton h-5 w-12 rounded-full" />
        <div className="skeleton h-5 w-16 rounded-full" />
      </div>
    </div>
  )
}
